import type { AppEdge } from '@/edges';
import type { AppNode } from '@/nodes';
import type { AppFlowObjectKnown } from '@/util/AppFlowObjectKnown';
import { findDisconnectedDataKeys } from '@/util/dataKeyUtils';
import logger from '@/util/logger';
import {
    type AppNodeType,
    edgeDataSchemas,
    getAllOutgoingHandles,
    getGuideType,
    type Guide,
    type GuideNodeId,
    GuideSchema,
    isNodeAllowedInGuide,
    nodeDataSchemas,
    type OutgoingHandle,
} from '@rock/types';
import { type Dictionary, includes, keyBy } from 'lodash';
import * as z from 'zod';

const log = logger('convertFlowToGuide');

export class GuideValidationError extends Error {}

export function convertFlowToGuide(flow: AppFlowObjectKnown): Guide {
    validateFlow(flow);
    const guideObject = buildGuideObject(flow);
    return GuideSchema.decode(guideObject);
}

function buildGuideObject(flow: AppFlowObjectKnown): Guide {
    const { space, equipmentTypeId, id: flowId, name, nodes, edges } = flow;
    return {
        object: 'guide',
        schema: 1,
        id: flowId,
        space: space,
        equipmentTypeId,
        flowId,
        name,
        nodes: nodes.map((n) => ({
            id: n.id as GuideNodeId,
            data: n.data,
        })),
        edges: edges
            .filter((e) => !!e.sourceHandle && !!e.target)
            .map((e) => ({
                id: e.id,
                sourceHandleId: e.sourceHandle as OutgoingHandle,
                target: e.target as GuideNodeId,
                data: e.data || {
                    type: 'dataKey',
                    components: [],
                    items: { equipment: [], considerations: [] },
                },
            })),
        publishedAt: new Date().toISOString(),
    };
}

function validateFlow(flow: AppFlowObjectKnown) {
    // Prepare a flow without ignored edges and RETURN backward edges for cycle detection
    const returnEdgeNodeIds = new Set(
        flow.nodes.filter((n) => n.type === 'return' && n.data?.returnType === 'edge').map((n) => n.id),
    );
    const cycleEdges = (flow.edges as AppEdge[]).filter(
        (edge) => edge.data?.ignoreForValidation !== true && !returnEdgeNodeIds.has(edge.source),
    );
    const cycleFlow: AppFlowObjectKnown = { ...flow, edges: cycleEdges };

    // Basic structure and data checks
    checkForElements(cycleFlow);
    checkNodeAndEdgeData(cycleFlow);
    checkNodeTypesForGuideType(cycleFlow);
    checkForEntryNode(cycleFlow);

    // Cycle detection on cycleFlow
    const cycleTraverser = new FlowTraverser(cycleFlow);
    checkForCycles(cycleTraverser);

    // Check that all handles have edges for reachable nodes
    checkAllHandlesHaveEdges(flow);

    // Exit-node detection on full flow, treating ignored edges as valid exits
    const exitTraverser = new FlowTraverser(flow);
    const missingExitNodeIds = findMissingExitNodesWithIgnore(exitTraverser);
    if (missingExitNodeIds.length) {
        throw new GuideValidationError(
            `There are nodes on the graph that don't link to an exit node: [${missingExitNodeIds.join(', ')}]`,
        );
    }

    // Data key reference validation
    checkDataKeyReferences(flow);
}

function checkForElements(flow: AppFlowObjectKnown) {
    if ((flow.nodes?.length ?? 0) < 1 || (flow?.edges?.length ?? 0) < 1) {
        throw new GuideValidationError(`You need nodes and edges silly`);
    }
}

function checkNodeAndEdgeData(flow: AppFlowObjectKnown) {
    for (const node of flow.nodes) {
        try {
            nodeDataSchemas[node.type!]?.parse(node.data || {});
        } catch (err) {
            if (err instanceof z.ZodError) {
                log.error('Node Validation Error', { err });
                throw new GuideValidationError(`Node: ${node.id} has validation errors. Please correct those first.`);
            } else {
                throw err;
            }
        }
    }
    for (const edge of flow.edges) {
        try {
            edgeDataSchemas[edge.type!]?.parse(edge.data || {});
        } catch (err) {
            if (err instanceof z.ZodError) {
                log.error('Edge Validation Error', { err });
                throw new GuideValidationError(`Edge: ${edge.id} has validation errors. Please correct those first.`);
            } else {
                throw err;
            }
        }
    }
}

function checkForEntryNode(flow: AppFlowObjectKnown) {
    const entryNodes = flow.nodes.filter((n) => n.type === 'entry');
    if (entryNodes.length !== 1) {
        throw new GuideValidationError(`Exactly one Entry node is required.  You have: ${entryNodes.length}`);
    }
}

function checkNodeTypesForGuideType(flow: AppFlowObjectKnown) {
    const guideType = getGuideType({ spaceId: flow.space, equipmentTypeId: flow.equipmentTypeId });
    const invalidNodes = flow.nodes.filter((n) => n.type && !isNodeAllowedInGuide(n.type as AppNodeType, guideType));
    if (invalidNodes.length > 0) {
        const names = invalidNodes.map((n) => `${n.type} (${n.id})`).join(', ');
        throw new GuideValidationError(`The following nodes are not allowed in a ${guideType} guide: ${names}`);
    }
}

function checkAllHandlesHaveEdges(flow: AppFlowObjectKnown) {
    const traverser = new FlowTraverser(flow);
    const reachableNodeIds = findReachableNodes(traverser);
    const edgesByHandle = keyBy(flow.edges, 'sourceHandle');
    const missingEdges: Array<{ nodeId: string; handle: OutgoingHandle }> = [];

    for (const nodeId of reachableNodeIds) {
        const node = traverser.nodesById[nodeId];
        if (!node) continue;

        const handles = getAllOutgoingHandles(node.id as GuideNodeId, node.data);
        for (const handle of handles) {
            if (!edgesByHandle[handle]) {
                missingEdges.push({ nodeId: node.id, handle });
            }
        }
    }

    if (missingEdges.length > 0) {
        const messages = missingEdges.map(({ nodeId, handle }) => `Node ${nodeId}: handle ${handle}`);
        throw new GuideValidationError(`The following handles are missing edges:\n${messages.join('\n')}`);
    }
}

function checkDataKeyReferences(flow: AppFlowObjectKnown) {
    const disconnected = findDisconnectedDataKeys(flow.nodes as AppNode[]);
    if (disconnected.length > 0) {
        const messages = disconnected.map((d) => `${d.nodeId} references "${d.key}"`);
        throw new GuideValidationError(
            `The following nodes reference data keys that are not produced by any node:\n${messages.join('\n')}`,
        );
    }
}

function findReachableNodes(traverser: FlowTraverser): Set<string> {
    const reachable = new Set<string>();
    const visited = new Set<string>();

    const dfs = (nodeId: string) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        reachable.add(nodeId);

        const outgoingPaths = traverser.getOutgoingPaths(nodeId);
        for (const { edge } of outgoingPaths) {
            if (edge) {
                dfs(edge.target);
            }
        }
    };

    dfs(traverser.entry.id);
    return reachable;
}

function checkForCycles(traverser: FlowTraverser) {
    const cycle = findCycle(traverser);
    if (cycle) {
        throw new GuideValidationError(
            `The graph cannot have cycles.  Found cycle: [${cycle.map((e) => e.id).join(' => ')}]`,
        );
    }
}

function findCycle(traverser: FlowTraverser): AppEdge[] | null {
    const stack: string[] = [];
    let cycleEdges: AppEdge[] | null = null;

    const dfs = (nodeId: string, path: AppEdge[]): boolean => {
        if (includes(stack, nodeId)) {
            // Found a cycle, extract edges forming the cycle
            const cycleStartIndex = path.findIndex((e) => e.source === nodeId);
            if (cycleStartIndex !== -1) {
                cycleEdges = path.slice(cycleStartIndex);
            }
            return true;
        }
        stack.push(nodeId);

        const outgoingPaths = traverser.getOutgoingPaths(nodeId);
        // console.log(`Outgoing node ${nodeId}: [${outgoingPaths.map(p => `${p.handle} ${p.edge ? '✅' : '❌'}/${p.target ? '✅' : '❌'}`).join(', ')}]`);
        for (const { edge } of outgoingPaths) {
            if (edge) {
                path.push(edge);
                if (dfs(edge.target, path)) return true;
                path.pop();
            }
        }

        stack.pop();
        return false;
    };

    if (dfs(traverser.entry.id, [])) {
        return cycleEdges; // Found a cycle
    }

    return null; // No cycles found
}

// function checkForExitNodes(traverser: FlowTraverser) {
//     const missingExitNodeIds = findMissingExitNodes(traverser);
//     if (missingExitNodeIds.length) {
//         throw new GuideValidationError(
//             `There are nodes on the graph that don't link to an exit node: [${missingExitNodeIds.join(', ')}]`,
//         );
//     }
// }
//
//
// function findMissingExitNodes(traverser: FlowTraverser): string[] {
//     const nonExitPaths: string[] = [];
//
//     const dfs = (nodeId: string): boolean => {
//         const node = traverser.nodesById[nodeId];
//         if (!node) return false;
//
//         const outgoingPaths = traverser.getOutgoingPaths(nodeId);
//         // console.log(`Outgoing node ${nodeId}: [${outgoingPaths.map(p => `${p.handle} ${p.edge ? '✅' : '❌'}/${p.target ? '✅' : '❌'}`).join(', ')}]`);
//         if (outgoingPaths.length === 0 && node.type !== 'exit' && node.type !== 'return') {
//             nonExitPaths.push(nodeId);
//             return false;
//         }
//
//         let allPathsLeadToExit = true;
//
//         for (const { edge } of outgoingPaths) {
//             if (!edge) {
//                 nonExitPaths.push(nodeId);
//                 return false;
//             } else if (!dfs(edge.target)) {
//                 allPathsLeadToExit = false;
//             }
//         }
//
//         return allPathsLeadToExit;
//     };
//
//     dfs(traverser.entry.id);
//
//     return nonExitPaths.length > 0 ? nonExitPaths : [];
// }

/**
 * Similar to findMissingExitNodes but treats any ignored edge
 * as leading to an exit node (i.e., that branch is considered valid).
 */
function findMissingExitNodesWithIgnore(traverser: FlowTraverser): string[] {
    const nonExitPaths: string[] = [];

    const dfs = (nodeId: string): boolean => {
        const node = traverser.nodesById[nodeId];
        if (!node) return false;
        // Exit or return nodes are valid endpoints
        if (node.type === 'exit' || node.type === 'return') {
            return true;
        }

        const outgoingPaths = traverser.getOutgoingPaths(nodeId);
        // Separate paths into ignored and valid ones
        const nonIgnored = outgoingPaths.filter(({ edge }) => edge && edge.data?.ignoreForValidation !== true);
        const hasIgnored = outgoingPaths.some(({ edge }) => edge && edge.data?.ignoreForValidation === true);

        // If there are only ignored paths, consider this node valid (assumed to lead to exit)
        if (nonIgnored.length === 0 && hasIgnored) {
            return true;
        }
        // If no paths at all, it's missing an exit
        if (nonIgnored.length === 0) {
            nonExitPaths.push(nodeId);
            return false;
        }

        // Recurse on non-ignored paths
        let allLeadToExit = true;
        for (const { edge } of nonIgnored) {
            if (edge) {
                if (!dfs(edge.target)) {
                    allLeadToExit = false;
                }
            } else {
                nonExitPaths.push(nodeId);
                allLeadToExit = false;
            }
        }
        return allLeadToExit;
    };

    dfs(traverser.entry.id);
    return nonExitPaths.length > 0 ? nonExitPaths : [];
}

class FlowTraverser {
    readonly entry: AppNode;
    readonly nodesById: Dictionary<AppNode>;
    private readonly edgesByHandle: Dictionary<AppEdge>;
    private flow: AppFlowObjectKnown;

    constructor(flow: AppFlowObjectKnown) {
        this.flow = flow;
        const entry = this.flow.nodes.find((n) => n.type === 'entry');
        if (!entry) {
            throw new Error('No entry node found');
        }
        this.entry = entry;
        this.nodesById = keyBy(this.flow.nodes, 'id');
        this.edgesByHandle = keyBy(this.flow.edges, 'sourceHandle');
    }

    getOutgoingPaths(nodeId: string): { handle: OutgoingHandle; edge?: AppEdge; target?: AppNode }[] {
        const node = this.nodesById[nodeId];
        if (!node) {
            throw new Error(`No node found with id: ${nodeId}`);
        }

        const handles = getAllOutgoingHandles(nodeId as GuideNodeId, node.data);
        return handles.map((handle) => {
            const edge = this.edgesByHandle[handle];
            const target = edge ? this.nodesById[edge.target] : undefined;
            return { handle, edge, target };
        });
    }
}
